import React, { useState, useEffect } from 'react';
import { 
  Trash2, Plus, CheckCircle2, Loader2, Layers, Settings2, 
  ImageIcon, Type, AlignLeft, AlignCenter, AlignRight, X, 
  Monitor, Cpu, ShieldCheck, Video, MoveVertical, Type as FontIcon, 
  ChevronDown, ChevronUp, Image as ImagePlaceholder, Bold, Scissors
} from 'lucide-react';
import { toast } from 'react-toastify';
import { http } from '../../axios/axios';

const AdminEditPage = ({ slug, name }) => {
  const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    slug: slug || '',
    category: 'Security Services',
    theme: { primaryColor: '#10407a', accentColor: '#EF8913', fontFamily: 'Inter' },
    sections: []
  });

  const [fileMap, setFileMap] = useState({});

  useEffect(() => { if (slug) fetchPageData(); }, [slug]);

  const fetchPageData = async () => {
    setIsLoading(true);
    try {
      const res = await http.get(`/dynamic-pages/${slug}`);
      const result = res.data?.data || res.data;
      if (result) setFormData(result);
    } catch (err) { 
      toast.error("Failed to fetch node data."); 
    } finally {
      setIsLoading(false);
    }
  };

  // --- FILE HANDLING ---
  const handleFileChange = (secIdx, files) => {
    const key = `section_${secIdx}_media`;
    setFileMap(prev => ({ ...prev, [key]: Array.from(files) }));
    toast.info(`${files.length} assets queued`);
  };

  const handleCardFileChange = (secIdx, cardIdx, files) => {
    const key = `section_${secIdx}_item_${cardIdx}_image`;
    setFileMap(prev => ({ ...prev, [key]: [files[0]] }));
    toast.info(`Card image queued`);
  };

  const removeExistingMedia = (secIdx, mediaIdx) => {
    const updated = [...formData.sections];
    updated[secIdx].media.splice(mediaIdx, 1);
    setFormData({ ...formData, sections: updated });
  };

  // --- SECTION MANAGEMENT ---
  const addNewSection = (type) => {
    const newSection = {
      type,
      enabled: true,
      config: { layoutType: 'row', columns: 1, backgroundColor: '#020617', padding: '100px 0px' },
      heading: { text: `NEW ${type.toUpperCase()}`, color: '#ffffff', fontSize: '48px', fontWeight: '900', textAlign: 'left' },
      subheading: { text: 'Subtext description...', color: '#94a3b8', fontSize: '18px', textAlign: 'left' },
      description: { text: '', color: '#64748b', fontSize: '16px', textAlign: 'left' },
      media: [],
      items: []
    };
    setFormData({ ...formData, sections: [...formData.sections, newSection] });
    setShowAddModal(false);
  };

  const updateSectionField = (secIdx, field, prop, value) => {
    const updated = [...formData.sections];
    updated[secIdx][field][prop] = value;
    setFormData({ ...formData, sections: updated });
  };

  const handleUpdate = async () => {
    setIsSaving(true);
    try {
      const data = new FormData();
      data.append('data', JSON.stringify(formData));
      Object.entries(fileMap).forEach(([key, files]) => {
        files.forEach(file => data.append(key, file));
      });
      const res = await http.put(`/dynamic-pages/${slug}`, data);
      setFormData(res.data?.data || res.data);
      setFileMap({});
      toast.success("🛡️ Node Synchronized Successfully");
    } catch (err) { 
      toast.error("Sync Failure"); 
    } finally { 
      setIsSaving(false); 
    }
  };

  if (isLoading) return <div className="flex justify-center p-20"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-32">
      {/* HEADER CONTROL BAR */}
      <div className="bg-[#11141d] border border-white/5 p-8 rounded-[2.5rem] sticky top-24 z-[40] shadow-2xl flex justify-between items-center">
        <div className="flex items-center gap-5">
          <Layers className="text-blue-500" size={28} />
          <div>
            <h1 className="text-xl font-black text-white uppercase italic">{name || slug}</h1>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Suraksha 24x7 Core Engine</p>
          </div>
        </div>
        <div className="flex gap-4">
          <button onClick={() => setShowAddModal(true)} className="bg-white/5 hover:bg-white/10 p-4 rounded-2xl text-blue-400 border border-white/5 transition-colors"><Plus/></button>
          <button onClick={handleUpdate} disabled={isSaving} className="bg-blue-600 hover:bg-blue-500 text-white px-10 py-4 rounded-2xl font-black uppercase text-[11px] tracking-widest flex items-center gap-3 transition-all">
            {isSaving ? <Loader2 className="animate-spin" size={18}/> : <CheckCircle2 size={18}/>}
            {isSaving ? "Uploading..." : "Update Live Site"}
          </button>
        </div>
      </div>

      <div className="space-y-12">
        {formData.sections?.map((section, sIdx) => (
          <div key={sIdx} className="bg-[#11141d] rounded-[3rem] p-10 border border-white/5 relative overflow-hidden">
            {/* SECTION TITLE & DELETE */}
            <div className="flex items-center justify-between mb-10 border-b border-white/5 pb-6">
               <div className="flex items-center gap-4 flex-1">
                  <span className="bg-blue-600/10 text-blue-500 text-[9px] font-black px-4 py-1 rounded-full uppercase border border-blue-500/20">{section.type}</span>
                  <input className="bg-transparent text-xl font-black text-white outline-none w-full" value={section.heading.text} onChange={(e) => updateSectionField(sIdx, 'heading', 'text', e.target.value)} />
               </div>
               <button onClick={() => setFormData({ ...formData, sections: formData.sections.filter((_, i) => i !== sIdx) })} className="text-slate-600 hover:text-red-500 transition-all"><Trash2 size={20}/></button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-8 space-y-8">
                {/* 1. HEADING STYLE */}
                <TypographyControls title="Heading Styling" data={section.heading} onChange={(prop, val) => updateSectionField(sIdx, 'heading', prop, val)} />

                {/* 2. SUBHEADING AREA */}
                <div className="bg-black/20 p-8 rounded-3xl border border-white/5 space-y-4">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Subheading Text</label>
                  <textarea className="w-full bg-[#0a0c12] p-4 rounded-xl text-white text-sm outline-none border border-white/5 focus:border-blue-500" value={section.subheading.text} onChange={(e) => updateSectionField(sIdx, 'subheading', 'text', e.target.value)} />
                  <TypographyControls title="Subheading Styling" data={section.subheading} hideText={true} onChange={(prop, val) => updateSectionField(sIdx, 'subheading', prop, val)} />
                </div>

                {/* 3. MEDIA MANAGER */}
                <div className="bg-black/20 p-8 rounded-3xl border border-white/5 space-y-6">
                   <h4 className="text-[10px] font-black text-emerald-500 uppercase tracking-widest flex items-center gap-2"><ImageIcon size={14}/> Section Media</h4>
                   <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {section.media?.map((m, mIdx) => (
                        <div key={mIdx} className="relative aspect-video rounded-xl overflow-hidden bg-black/40 border border-white/5 group/img">
                           {m.fileType === 'video' ? <div className="flex items-center justify-center h-full text-blue-500"><Video size={24}/></div> : <img src={`${BACKEND_URL}/${m.filePath}`} className="w-full h-full object-cover" />}
                           <button onClick={() => removeExistingMedia(sIdx, mIdx)} className="absolute inset-0 bg-red-600/80 opacity-0 group-hover/img:opacity-100 flex items-center justify-center text-white transition-opacity"><Trash2 size={16}/></button>
                        </div>
                      ))}
                      <label className="aspect-video border-2 border-dashed border-white/5 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-500/5 transition-all">
                         <Plus className="text-slate-600"/><input type="file" multiple className="hidden" onChange={(e) => handleFileChange(sIdx, e.target.files)} />
                      </label>
                   </div>
                </div>

                {/* 4. REPEATER / CARDS */}
                <div className="bg-black/20 p-8 rounded-3xl border border-white/5 space-y-4">
                   <div className="flex justify-between items-center mb-4">
                      <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Repeater Items</h4>
                      <button onClick={() => {
                        const updated = [...formData.sections];
                        updated[sIdx].items.push({
                          title: { text: 'New Card', color: '#ffffff', fontSize: '20px', fontWeight: '700' },
                          body: { text: 'Description...', color: '#94a3b8', fontSize: '14px' },
                          image: { filePath: '', fileType: 'image' },
                          button: { text: 'Action', link: '#', style: { bgColor: '#3b82f6', textColor: '#ffffff' } }
                        });
                        setFormData({ ...formData, sections: updated });
                      }} className="text-blue-500 text-[10px] font-black uppercase flex items-center gap-2 bg-blue-500/10 px-4 py-2 rounded-lg hover:bg-blue-500/20 transition-all"><Plus size={12}/> Add Card</button>
                   </div>
                   <div className="grid grid-cols-1 gap-6">
                      {section.items?.map((item, iIdx) => (
                         <div key={iIdx} className="bg-[#0a0c12] p-8 rounded-[2rem] border border-white/5 relative group/item">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                               <div className="space-y-2">
                                  <label className="text-[7px] font-black uppercase text-slate-600">Card Asset</label>
                                  <div className="relative aspect-video bg-black rounded-2xl border border-white/5 overflow-hidden flex items-center justify-center group/cardimg">
                                     {item.image?.filePath ? (
                                        <img src={`${BACKEND_URL}/${item.image.filePath}`} className="w-full h-full object-cover opacity-60" />
                                     ) : (
                                        <div className="text-slate-700 flex flex-col items-center gap-2"><ImageIcon size={24}/><span className="text-[8px] uppercase font-black">No Image</span></div>
                                     )}
                                     <label className="absolute inset-0 bg-blue-600/80 opacity-0 group-hover/cardimg:opacity-100 flex flex-col items-center justify-center text-white cursor-pointer transition-all gap-2">
                                        <Plus size={20}/><input type="file" className="hidden" onChange={(e) => handleCardFileChange(sIdx, iIdx, e.target.files)} />
                                     </label>
                                  </div>
                               </div>
                               <div className="space-y-4">
                                  <PropInput label="Title" value={item.title.text} onChange={(v) => { const s = [...formData.sections]; s[sIdx].items[iIdx].title.text = v; setFormData({...formData, sections: s}); }} />
                                  <textarea className="w-full bg-black/40 p-3 rounded-lg text-slate-400 text-xs border border-white/5 h-20 outline-none" value={item.body.text} onChange={(e) => { const s = [...formData.sections]; s[sIdx].items[iIdx].body.text = e.target.value; setFormData({...formData, sections: s}); }} />
                               </div>
                               <div className="space-y-4">
                                  <PropInput label="Button Label" value={item.button?.text} onChange={(v) => { const s = [...formData.sections]; s[sIdx].items[iIdx].button.text = v; setFormData({...formData, sections: s}); }} />
                                  <ColorInput label="Button Color" value={item.button?.style?.bgColor} onChange={(v) => { const s = [...formData.sections]; s[sIdx].items[iIdx].button.style.bgColor = v; setFormData({...formData, sections: s}); }} />
                               </div>
                            </div>
                            <button onClick={() => { const s = [...formData.sections]; s[sIdx].items.splice(iIdx, 1); setFormData({...formData, sections: s}); }} className="absolute top-4 right-4 text-slate-700 hover:text-red-500"><X size={18}/></button>
                         </div>
                      ))}
                   </div>
                </div>
              </div>

              {/* SIDEBAR: LAYOUT CONFIG */}
              <div className="lg:col-span-4 space-y-6">
                <div className="bg-black/20 p-8 rounded-[2rem] border border-white/5 space-y-6">
                   <h4 className="text-[10px] font-black text-slate-500 uppercase flex items-center gap-2"><Settings2 size={14}/> Layout Config</h4>
                   <PropInput label="Grid Columns (1-4)" value={section.config.columns} onChange={(v) => { const s = [...formData.sections]; s[sIdx].config.columns = Number(v); setFormData({...formData, sections: s}); }} />
                   <PropInput label="Section Padding" value={section.config.padding} onChange={(v) => { const s = [...formData.sections]; s[sIdx].config.padding = v; setFormData({...formData, sections: s}); }} />
                   <ColorInput label="Section BG" value={section.config.backgroundColor} onChange={(v) => { const s = [...formData.sections]; s[sIdx].config.backgroundColor = v; setFormData({...formData, sections: s}); }} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ADD SECTION MODAL */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[100] flex items-center justify-center p-6">
          <div className="bg-[#11141d] border border-white/5 w-full max-w-xl rounded-[3rem] p-10 relative">
            <button onClick={() => setShowAddModal(false)} className="absolute top-8 right-8 text-slate-500"><X/></button>
            <h2 className="text-xl font-black text-white uppercase mb-8 text-center">New Infrastructure Section</h2>
            <div className="grid grid-cols-2 gap-4">
               {['hero', 'services', 'stats', 'cta'].map(id => (
                 <button key={id} onClick={() => addNewSection(id)} className="bg-white/5 p-6 rounded-2xl border border-white/5 hover:border-blue-500 uppercase text-[10px] font-black tracking-widest text-slate-400 hover:text-white transition-all">
                    {id} Module
                 </button>
               ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- SUB-COMPONENTS ---
const TypographyControls = ({ title, data, onChange, hideText = false }) => (
  <div className="bg-black/20 p-6 rounded-3xl border border-white/5 space-y-6">
    <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-widest flex items-center gap-2"><FontIcon size={14}/> {title}</h4>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {!hideText && <PropInput label="Text" value={data.text} onChange={(v) => onChange('text', v)} />}
      <PropInput label="Size" value={data.fontSize} onChange={(v) => onChange('fontSize', v)} />
      <PropInput label="Weight" value={data.fontWeight} onChange={(v) => onChange('fontWeight', v)} />
      <ColorInput label="Color" value={data.color} onChange={(v) => onChange('color', v)} />
      <div className="space-y-1">
        <label className="text-[7px] font-black uppercase text-slate-600 block">Alignment</label>
        <div className="flex bg-black/40 p-1 rounded-lg border border-white/5">
           {['left', 'center', 'right'].map(align => (
             <button key={align} onClick={() => onChange('textAlign', align)} className={`flex-1 p-1 rounded flex justify-center ${data.textAlign === align ? 'bg-blue-600 text-white' : 'text-slate-600'}`}>
               {align === 'left' ? <AlignLeft size={12}/> : align === 'center' ? <AlignCenter size={12}/> : <AlignRight size={12}/>}
             </button>
           ))}
        </div>
      </div>
    </div>
  </div>
);

const PropInput = ({ label, value, onChange }) => (
  <div className="space-y-1 flex-1">
    <label className="text-[7px] font-black uppercase text-slate-600 block">{label}</label>
    <input className="w-full bg-black/40 border border-white/5 p-2.5 rounded-xl text-[11px] text-white outline-none focus:border-blue-500 transition-all" value={value || ''} onChange={(e) => onChange(e.target.value)} />
  </div>
);

const ColorInput = ({ label, value, onChange }) => (
  <div className="space-y-1">
    <label className="text-[7px] font-black uppercase text-slate-600 block">{label}</label>
    <div className="flex items-center gap-2 bg-black/40 p-2 rounded-xl border border-white/5">
      <input type="color" className="w-4 h-4 bg-transparent cursor-pointer" value={value || '#ffffff'} onChange={(e) => onChange(e.target.value)} />
      <span className="text-[9px] font-mono text-slate-500 uppercase">{value || '#ffffff'}</span>
    </div>
  </div>
);

export default AdminEditPage;